import { Trans } from '@lingui/react/macro';

import type { ImageAnalyses } from '@/@types/latestImagesV2';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';

import type {
  DateFilterState,
  RadiographSubtype,
  SelectionModalActions,
  SelectionModalSelectors,
} from '../../../hooks/useImageSelectionModalState';
import { FmxLayoutModal } from '../claimAttachment/insuranceImagesSection/FmxLayout';

import { ImageGridSubsection } from './ImageGridSubsection';
import { SelectableImageCard } from './SelectableImageCard';

export type RadiographSectionItem = {
  sectionTitle: React.ReactNode;
  radiographTypeKey: 'BITEWING' | 'PERIAPICAL' | 'PANORAMIC';
};

type RadiographsSectionProps = {
  radiographSections: RadiographSectionItem[];
  dateStr: string;
  filters: DateFilterState;
  selectors: SelectionModalSelectors;
  filteredRadiographs: ImageAnalyses[];
  filteredColorImages: ImageAnalyses[];
  claimSummary: GetClaimSummaryResponse | undefined;
  selectedTreatmentIds: string[];
  fmxGrid: (ImageAnalyses | null)[];
  actions: SelectionModalActions;
  viewFilterImageTypes: Set<string>;
};

export const RadiographsSection = ({
  radiographSections,
  dateStr,
  filters,
  selectors,
  filteredRadiographs,
  filteredColorImages,
  claimSummary,
  selectedTreatmentIds,
  fmxGrid,
  actions,
  viewFilterImageTypes,
}: RadiographsSectionProps) => {
  const showFmx = selectors.presentTypesByDate(dateStr).includes('fmx');
  const hasFmx = showFmx && filters['fmx-present'] && viewFilterImageTypes.has('fmx');
  const visibleRadiographSections = radiographSections.filter(({ radiographTypeKey }) =>
    viewFilterImageTypes.has(radiographTypeKey),
  );
  const hasAnyRadiographs = visibleRadiographSections.some(
    ({ radiographTypeKey }) =>
      Object.keys(filters.radiographs?.[radiographTypeKey] ?? {}).length > 0,
  );
  if (!hasAnyRadiographs && !hasFmx) return null;

  return (
    <div className="vai-flex vai-flex-col vai-gap-2">
      <h3 className="vai-font-[proxima-nova] vai-text-sm vai-font-semibold vai-text-zinc-600 vai-mb-1">
        <Trans>RADIOGRAPHS</Trans>
      </h3>
      {visibleRadiographSections.map(({ sectionTitle, radiographTypeKey }) => {
        const entries = filters.radiographs?.[radiographTypeKey] ?? {};
        return (
          <ImageGridSubsection
            key={radiographTypeKey}
            title={sectionTitle}
            entries={entries}
            claimSummary={claimSummary}
            filteredRadiographs={filteredRadiographs}
            filteredColorImages={filteredColorImages}
            selectedTreatmentIds={selectedTreatmentIds}
            onToggle={(imageID) =>
              actions.toggleRadiographId(
                dateStr,
                radiographTypeKey as RadiographSubtype,
                imageID,
              )
            }
          />
        );
      })}

      {hasFmx && (
        <div className="vai-flex vai-flex-col vai-gap-2">
          <h3 className="vai-text-sm vai-font-semibold vai-text-foreground">
            <Trans>FMX</Trans>
          </h3>
          <div className="vai-grid md:vai-grid-cols-2 vai-gap-3">
            <SelectableImageCard
              checked={selectors.fmxState(dateStr).selected}
              onChange={() => actions.toggleFmx(dateStr)}
            >
              <FmxLayoutModal fmxGrid={fmxGrid} header="" annotated={false} />
            </SelectableImageCard>
          </div>
        </div>
      )}
    </div>
  );
};
